import React, { useState } from "react";
import { getBucketLabel } from "./bucketColors";
import CountUp from "../../ui/CountUp";

interface BucketData {
  bucket: string;
  count: number;
  leadCount?: number; // conversations where lead sent last message
  color: string;
}

interface BucketSummaryProps {
  /** Header — plain string or JSX (DMHealthTab passes a <CountUp>-laced node) */
  setterName: React.ReactNode;
  setterId?: string;
  buckets: BucketData[];
  totalCount: number;
  /** Called with bucket name — supports _lead/_setter suffix for split bars */
  onBucketClick?: (bucket: string, setterId?: string) => void;
  /** Controlled window mode — renders lifetime/window pill only when BOTH props provided */
  windowMode?: "lifetime" | "window";
  onWindowModeChange?: (m: "lifetime" | "window") => void;
}

export const BucketSummary: React.FC<BucketSummaryProps> = ({
  setterName,
  setterId,
  buckets,
  totalCount,
  onBucketClick,
  windowMode,
  onWindowModeChange,
}) => {
  const [useLogScale, setUseLogScale] = useState(false);

  // Render the lifetime/window pill only when parent explicitly opts in
  const showWindowPill = windowMode !== undefined && onWindowModeChange !== undefined;

  // Hide zero-count buckets before rendering
  const visibleBuckets = buckets.filter((b) => b.count > 0);

  const maxCount = Math.max(...visibleBuckets.map((b) => b.count), 1);

  const hasSplitData = visibleBuckets.some((b) => b.leadCount != null && b.leadCount > 0);

  const getBarWidthPct = (count: number) => {
    if (useLogScale) {
      return (Math.log(count + 1) / Math.log(maxCount + 1)) * 100;
    }
    return (count / maxCount) * 100;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="token-14 font-semibold text-glass-secondary">{setterName}</h4>
        <div className="flex items-center gap-2 shrink-0">
          {/* lifetime/window pill — only renders when parent opts in via both props */}
          {showWindowPill && (
            <button
              onClick={() => onWindowModeChange!(windowMode === "window" ? "lifetime" : "window")}
              className="anim-tap relative flex items-center bg-black/[0.06] rounded-full h-6 w-[120px] shrink-0 select-none"
              aria-label={
                windowMode === "window" ? "Switch to lifetime totals" : "Switch to in-window totals"
              }
            >
              {/* Sliding pill */}
              <div
                className="absolute top-0.5 h-5 w-[58px] bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out"
                style={{
                  transform: windowMode === "window" ? "translateX(60px)" : "translateX(2px)",
                }}
              />
              <span
                className={`relative z-10 flex-1 text-center token-9 transition-colors ${
                  windowMode !== "window" ? "text-glass font-medium" : "text-glass-muted"
                }`}
              >
                lifetime
              </span>
              <span
                className={`relative z-10 flex-1 text-center token-9 transition-colors ${
                  windowMode === "window" ? "text-glass font-medium" : "text-glass-muted"
                }`}
              >
                window
              </span>
            </button>
          )}
          {/* iOS-style sliding toggle: exact ↔ balanced */}
          <button
            onClick={() => setUseLogScale((v) => !v)}
            className="relative flex items-center bg-black/[0.06] rounded-full h-6 w-[120px] shrink-0 select-none"
            aria-label={useLogScale ? "Switch to exact scale" : "Switch to balanced scale"}
          >
            {/* Sliding pill */}
            <div
              className="absolute top-0.5 h-5 w-[58px] bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out"
              style={{ transform: useLogScale ? "translateX(60px)" : "translateX(2px)" }}
            />
            <span
              className={`relative z-10 flex-1 text-center token-9 transition-colors ${
                !useLogScale ? "text-glass font-medium" : "text-glass-muted"
              }`}
            >
              exact
            </span>
            <span
              className={`relative z-10 flex-1 text-center token-9 transition-colors ${
                useLogScale ? "text-glass font-medium" : "text-glass-muted"
              }`}
            >
              balanced
            </span>
          </button>
        </div>
      </div>

      {/* Legend — only show when there's split data */}
      {hasSplitData && (
        <div className="flex items-center gap-3 mb-2">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-sm bg-black/40 shrink-0" />
            <span className="token-9 text-glass-muted">lead replied last</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-sm bg-black/10 shrink-0" />
            <span className="token-9 text-glass-muted">we replied last</span>
          </div>
        </div>
      )}

      {/* Column header for the count */}
      <div className="flex items-center gap-2 mb-1">
        <span className="w-20 shrink-0" />
        <div className="flex-1" />
        <span className="token-9 text-glass-muted w-8 text-center">chats</span>
        <span className="w-3 shrink-0" />
      </div>

      <div className="space-y-1.5">
        {visibleBuckets.map((b, rowIdx) => {
          // Use split rendering when this metric has split data at all (e.g. reply_turn_distribution)
          // even if THIS bucket has leadCount=0 — otherwise the setter-only bar renders
          // at full opacity looking like "lead replied last"
          const useSplitRender = hasSplitData && b.leadCount != null;
          const hasSplit = b.leadCount != null && b.leadCount > 0 && b.count > 0;
          const leadPct = hasSplit ? (b.leadCount! / b.count) * 100 : 0;
          const setterCount = useSplitRender ? b.count - (b.leadCount || 0) : b.count;
          const barWidthPct = getBarWidthPct(b.count);

          return (
            <div key={b.bucket} className="flex items-center gap-2">
              <span className="token-11 text-glass-muted w-20 text-right shrink-0">
                {getBucketLabel(b.bucket)}
              </span>
              <div className="flex-1 h-5 bg-black/[0.05] rounded overflow-hidden">
                {useSplitRender ? (
                  // Split bar: lead-last (solid) + setter-last (dimmed)
                  // Numbers inside each segment for readability
                  <div
                    className="anim-fill flex h-full"
                    style={{
                      width: `${barWidthPct}%`,
                      minWidth: "40px",
                      animationDelay: `${rowIdx * 45}ms`,
                    }}
                  >
                    {b.leadCount! > 0 && (
                      <div
                        className={`h-full ${b.color} ${onBucketClick ? "cursor-pointer hover:brightness-110" : ""} transition-all flex items-center px-1`}
                        style={{ width: `${leadPct}%`, minWidth: "22px" }}
                        title={`${b.leadCount} lead last — click to see`}
                        data-testid="proof-bucket-bar"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onBucketClick) onBucketClick(`${b.bucket}_lead`, setterId);
                        }}
                      >
                        <span className="token-9 font-semibold text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)] select-none leading-none">
                          {/* delay matches the row's .anim-fill stagger so number + bar land together */}
                          <CountUp value={b.leadCount!} delay={rowIdx * 45} />
                        </span>
                      </div>
                    )}
                    {setterCount > 0 && (
                      <div
                        className={`h-full ${b.color} opacity-30 ${onBucketClick ? "cursor-pointer hover:opacity-50" : ""} transition-all flex items-center justify-end px-1`}
                        style={{ width: `${100 - leadPct}%`, minWidth: "22px" }}
                        title={`${setterCount} setter last — click to see`}
                        data-testid="proof-bucket-bar"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onBucketClick) onBucketClick(`${b.bucket}_setter`, setterId);
                        }}
                      >
                        <span className="token-9 font-semibold text-black/50 select-none leading-none">
                          <CountUp value={setterCount} delay={rowIdx * 45} />
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  // Single bar (no split data, or non-reply-turn metric)
                  <div
                    className={`anim-fill h-full rounded ${b.color} transition-all ${
                      onBucketClick && b.count > 0 ? "cursor-pointer hover:brightness-110" : ""
                    }`}
                    style={{ width: `${barWidthPct}%`, animationDelay: `${rowIdx * 45}ms` }}
                    data-testid="proof-bucket-bar"
                    onClick={() =>
                      onBucketClick && b.count > 0 && onBucketClick(b.bucket, setterId)
                    }
                  />
                )}
              </div>
              <span className="token-11 font-medium text-glass-secondary w-8 text-center">
                <CountUp value={b.count} delay={rowIdx * 45} />
              </span>
              {onBucketClick && b.count > 0 && (
                <svg
                  className="w-3 h-3 text-glass-muted shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z"
                  />
                </svg>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer: total count */}
      <p className="token-11 text-glass-muted mt-2">
        <CountUp value={totalCount} /> total
      </p>
    </div>
  );
};
